/**
 * Simple Markdown to HTML converter for label descriptions
 * Handles basic markdown formatting without complex tool annotations
 */
export class MarkdownToHtmlConverter {
  constructor() {
    // DOM parser for creating HTML elements
    this.parser = new DOMParser();
  }

  /**
   * Convert markdown content to HTML for TipTap editor
   * @param {string} markdown - The markdown content
   * @returns {string} - HTML content suitable for TipTap editor
   */
  static convertToHtml(markdown) {
    if (!markdown || typeof markdown !== 'string') {
      return '<p></p>';
    }

    let html = markdown;

    // Convert basic markdown to HTML
    html = MarkdownToHtmlConverter.convertBasicMarkdownToHtml(html);

    // Ensure we have at least one paragraph
    if (!html.trim()) {
      return '<p></p>';
    }

    return html;
  }

  /**
   * Convert basic markdown formatting to HTML
   * @param {string} text - Text with markdown formatting
   * @returns {string} - HTML formatted text
   */
  static convertBasicMarkdownToHtml(text) {
    let html = text;

    // Convert line breaks to <br> but handle paragraph breaks properly
    html = html.replace(/\n\n+/g, '</p><p>');
    html = html.replace(/\n/g, '<br>');

    // Convert headings (must be at start of line or after paragraph break)
    html = html.replace(
      /(^|<\/p><p>)######\s(.+?)(<br>|<\/p>|$)/g,
      '$1<h6>$2</h6>$3'
    );
    html = html.replace(
      /(^|<\/p><p>)#####\s(.+?)(<br>|<\/p>|$)/g,
      '$1<h5>$2</h5>$3'
    );
    html = html.replace(
      /(^|<\/p><p>)####\s(.+?)(<br>|<\/p>|$)/g,
      '$1<h4>$2</h4>$3'
    );
    html = html.replace(
      /(^|<\/p><p>)###\s(.+?)(<br>|<\/p>|$)/g,
      '$1<h3>$2</h3>$3'
    );
    html = html.replace(
      /(^|<\/p><p>)##\s(.+?)(<br>|<\/p>|$)/g,
      '$1<h2>$2</h2>$3'
    );
    html = html.replace(
      /(^|<\/p><p>)#\s(.+?)(<br>|<\/p>|$)/g,
      '$1<h1>$2</h1>$3'
    );

    // Convert bold text (only if not escaped)
    html = html.replace(/(?<!\\)\*\*(.+?)(?<!\\)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/(?<!\\)__(.+?)(?<!\\)__/g, '<strong>$1</strong>');

    // Convert italic text (only if not escaped)
    html = html.replace(/(?<!\\)\*(.+?)(?<!\\)\*/g, '<em>$1</em>');
    html = html.replace(/(?<!\\)_(.+?)(?<!\\)_/g, '<em>$1</em>');

    // Convert inline code (only if not escaped)
    html = html.replace(/(?<!\\)`(.+?)(?<!\\)`/g, '<code>$1</code>');

    // Convert links
    html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>');

    // Remove escape characters for display
    html = html.replace(/\\(.)/g, '$1');

    // Convert unordered lists
    html = html.replace(/^\s*[*\-+]\s(.+?)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');

    // Convert ordered lists
    html = html.replace(/^\s*\d+\.\s(.+?)$/gm, '<li>$1</li>');
    // Note: This is a simplified approach - in reality we'd need better list detection

    // Convert blockquotes
    html = html.replace(/^>\s(.+?)$/gm, '<blockquote>$1</blockquote>');

    // Wrap content in paragraphs if not already wrapped
    if (!html.startsWith('<')) {
      html = `<p>${html}</p>`;
    } else if (
      !html.startsWith('<p>') &&
      !html.startsWith('<h') &&
      !html.startsWith('<ul>') &&
      !html.startsWith('<ol>') &&
      !html.startsWith('<blockquote>')
    ) {
      html = `<p>${html}</p>`;
    }

    // Clean up empty paragraphs and fix nested paragraph issues
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p>(<h[1-6]>)/g, '$1');
    html = html.replace(/(<\/h[1-6]>)<\/p>/g, '$1');
    html = html.replace(/<p>(<ul>)/g, '$1');
    html = html.replace(/(<\/ul>)<\/p>/g, '$1');
    html = html.replace(/<p>(<ol>)/g, '$1');
    html = html.replace(/(<\/ol>)<\/p>/g, '$1');
    html = html.replace(/<p>(<blockquote>)/g, '$1');
    html = html.replace(/(<\/blockquote>)<\/p>/g, '$1');

    return html;
  }
}

// Create a default instance for easy importing
const markdownToHtmlConverter = new MarkdownToHtmlConverter();
export default markdownToHtmlConverter;
